import { Head, Link, router } from "@inertiajs/react"
import { format } from "date-fns"
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card"
import { Button } from "@/components/ui/button"
import { Badge } from "@/components/ui/badge"
import { ArrowLeft, Edit, Trash2, Landmark } from "lucide-react"
import { AppLayout } from "@/layouts/app-layout"
import { liabilitiesPath, editLiabilityPath, liabilityPath } from "@/routes"
import type { BreadcrumbItem } from "@/types"

const breadcrumbs: BreadcrumbItem[] = [
  {
    title: "Dashboard",
    href: "/dashboard",
  },
  {
    title: "Liabilities",
    href: liabilitiesPath(),
  },
  {
    title: "Liability Details",
    href: "#",
  },
]

interface LiabilityShowProps {
  liability: {
    id: number
    name: string
    category: string
    balance: number
    interest_rate: number | null
    minimum_payment: number | null
    months_to_payoff: number | null
    created_at: string
    updated_at: string
  }
}

export default function LiabilityShow({ liability }: LiabilityShowProps) {
  const formatCurrency = (amount: number) =>
    new Intl.NumberFormat("en-US", {
      style: "currency",
      currency: "USD",
    }).format(amount)

  const formatPayoff = (months: number | null) => {
    if (months === null) return "Not enough info"
    if (months === 0) return "Paid off"
    const years = Math.floor(months / 12)
    const rem = months % 12
    if (years === 0) return `${rem} months`
    return rem === 0 ? `${years} years` : `${years} yr ${rem} mo`
  }

  return (
    <AppLayout breadcrumbs={breadcrumbs}>
      <Head title="Liability Details" />

      <div className="flex h-full flex-1 flex-col gap-6 overflow-x-auto rounded-xl p-6">
        {/* Header */}
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-4">
            <Button variant="outline" size="sm" asChild>
              <Link href={liabilitiesPath()}>
                <ArrowLeft className="mr-2 h-4 w-4" />
                Back to Liabilities
              </Link>
            </Button>
            <div>
              <h1 className="text-3xl font-bold">{liability.name}</h1>
              <p className="text-muted-foreground">Liability details</p>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <Button asChild>
              <Link href={editLiabilityPath(liability.id)}>
                <Edit className="mr-2 h-4 w-4" />
                Edit
              </Link>
            </Button>
            <Button
              variant="destructive"
              onClick={() => {
                if (
                  confirm(
                    "Are you sure you want to delete this liability? This action cannot be undone.",
                  )
                ) {
                  router.delete(liabilityPath(liability.id))
                }
              }}
            >
              <Trash2 className="mr-2 h-4 w-4" />
              Delete
            </Button>
          </div>
        </div>

        <div className="max-w-2xl">
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Landmark className="h-5 w-5" />
                Liability Information
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-6">
              <div className="flex items-center justify-between">
                <span className="text-muted-foreground text-sm font-medium">
                  Balance
                </span>
                <span className="text-2xl font-bold text-red-600 dark:text-red-400">
                  {formatCurrency(liability.balance)}
                </span>
              </div>
              <div className="flex items-center justify-between">
                <span className="text-muted-foreground text-sm font-medium">
                  Category
                </span>
                <Badge variant="outline">{liability.category}</Badge>
              </div>
              <div className="flex items-center justify-between">
                <span className="text-muted-foreground text-sm font-medium">
                  Interest Rate (APR)
                </span>
                <span className="text-sm">
                  {liability.interest_rate != null
                    ? `${liability.interest_rate}%`
                    : "—"}
                </span>
              </div>
              <div className="flex items-center justify-between">
                <span className="text-muted-foreground text-sm font-medium">
                  Minimum Monthly Payment
                </span>
                <span className="text-sm">
                  {liability.minimum_payment != null
                    ? formatCurrency(liability.minimum_payment)
                    : "—"}
                </span>
              </div>
              <div className="flex items-center justify-between">
                <span className="text-muted-foreground text-sm font-medium">
                  Estimated Payoff Time
                </span>
                <span className="text-sm">
                  {formatPayoff(liability.months_to_payoff)}
                </span>
              </div>
              <div className="text-muted-foreground border-t pt-4 text-xs">
                Added {format(new Date(liability.created_at), "MMM dd, yyyy")}
              </div>
            </CardContent>
          </Card>
        </div>
      </div>
    </AppLayout>
  )
}
